import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface EmailCaptureCTAProps {
  headline?: string;
  description?: string;
  source: string;
}

export function EmailCaptureCTA({ 
  headline = 'Get China Travel Tips', 
  description = 'Join our newsletter for exclusive guides, deals, and insider tips for traveling in China.',
  source 
}: EmailCaptureCTAProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('email_signups')
        .insert({ email, source });

      if (error) {
        if (error.code === '23505') {
          toast.info('You\'re already subscribed!');
        } else {
          throw error;
        }
      } else {
        setSuccess(true);
        toast.success('Successfully subscribed!');
        setEmail('');
      }
    } catch (err) {
      console.error('Error saving email:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card className="border-green-500/20 bg-gradient-to-br from-green-500/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 text-center justify-center">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <h3 className="font-semibold text-lg">You're In!</h3>
              <p className="text-sm text-muted-foreground">
                Check your inbox for your first China travel guide.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-primary/10 hidden sm:block">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1">{headline}</h3>
            <p className="text-sm text-muted-foreground mb-4">{description}</p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
                disabled={loading}
              />
              <Button type="submit" disabled={loading}>
                {loading ? 'Subscribing...' : 'Subscribe'}
                <Send className="ml-2 h-4 w-4" />
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-2">
              No spam, unsubscribe anytime.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
